import { copPerMonth, heatCoeficientPerMonth } from "../../../constants/heat";
import { pumpModels, PumpType } from "../../../constants/pumpData";

import { HeatData, PoolInfo, SeasonMonth } from "../../../context/types";

export const getPumpCapacity = (
  degressPowerSmall: number,
  degressPowerBig: number
) => {
  const filteredPumps = pumpModels.filter(
    (pump) =>
      pump.type === PumpType.AquathermPro || pump.type === PumpType.Aquatherm
  );

  const capacities = new Set<number>();
  filteredPumps.forEach((pump) => {
    capacities.add(pump.onAir15.capacity);
    if (pump.onAir26) {
      capacities.add(pump.onAir26.capacity);
    }
  });

  const pumps = Array.from(capacities);

  const data = pumps.map((pump) => {
    return {
      value1: Math.abs(degressPowerSmall - pump),
      value2: Math.abs(degressPowerBig - pump),
      capacity: pump,
    };
  });
  return data;
};

export const getTotalPrice = ({
  heatData,
  standartPumpCapacity,
  premiumPumpCapacity,
  poolInfo,
  months,
  counter,
  usesLowTemperatureCOP,
}: {
  heatData: HeatData;
  poolInfo: PoolInfo;
  standartPumpCapacity: number | string;
  premiumPumpCapacity?: number | string;
  months: SeasonMonth[];
  counter: number;
  usesLowTemperatureCOP?: boolean;
}): {
  totalCostStandard: number;
  totalCostPremium: number;
  bigModelEnergyConsumption: number[];
  smallModelEnergyConsumption: number[];
  standardCostPerMonth: number[];
  premiumCostPerMonth: number[];
} => {
  if (typeof standartPumpCapacity === "string" || standartPumpCapacity === 0) {
    return {
      totalCostPremium: 0,
      totalCostStandard: 0,
      bigModelEnergyConsumption: [],
      smallModelEnergyConsumption: [],
      standardCostPerMonth: [],
      premiumCostPerMonth: [],
    };
  }

  const standardModelCapacity = heatCoeficientPerMonth.map(
    (heatCoeficient) => Number(standartPumpCapacity) * heatCoeficient
  );

  const premiumModelCapacity = heatCoeficientPerMonth.map(
    (heatCoeficient) => Number(premiumPumpCapacity) * heatCoeficient
  );

  const averageTaxStandard = heatData.heatDemandLoss.map((loss, index) => {
    const capacity = standardModelCapacity[index];
    return capacity > 0 ? loss / capacity : 0;
  });

  const averageTaxPremium = heatData.heatDemandLoss.map((loss, index) => {
    const capacity = premiumModelCapacity[index];
    return capacity > 0 ? loss / capacity : 0;
  });

  const cOP26Standard = getCOPFromPumpData(
    Number(standartPumpCapacity),
    usesLowTemperatureCOP || false
  );
  const cOP26Premium = getCOPFromPumpData(
    Number(premiumPumpCapacity),
    usesLowTemperatureCOP || false
  );

  console.log("COP Debug:", {
    standartPumpCapacity,
    premiumPumpCapacity,
    cOP26Standard,
    cOP26Premium,
  });

  const defaultCOP = { value100: 4.0, value50: 5.5, value20: 7.0 }; // More realistic COPs
  const safeCOP26Standard = cOP26Standard || defaultCOP;
  const safeCOP26Premium = cOP26Premium || defaultCOP;

  const standardModelCOP = getStandardModelCOP(
    averageTaxStandard,
    safeCOP26Standard,
    copPerMonth
  );

  const premiumModelCOP = getPremiumModelCOP(
    averageTaxPremium,
    safeCOP26Premium,
    copPerMonth
  );

  const bigModelEnergyConsumption = premiumModelCOP?.map(
    (COP: number, index: number) => {
      return heatData.heatDemandLoss[index] > 0 && COP > 0
        ? (heatData.heatDemandLoss[index] / COP) * 24 * 30
        : 0;
    }
  );

  const smallModelEnergyConsumption = standardModelCOP?.map(
    (COP: number, index: number) => {
      return heatData.heatDemandLoss[index] > 0 && COP > 0
        ? (heatData.heatDemandLoss[index] / COP) * 24 * 30
        : 0;
    }
  );

  const standardCostPerMonth = smallModelEnergyConsumption.map(
    (consumption: number, index: number) =>
      months[index].value
        ? (consumption * poolInfo.electricityCost!) / counter
        : 0
  );
  const premiumCostPerMonth = bigModelEnergyConsumption.map(
    (consumption: number, index: number) =>
      months[index].value
        ? (consumption * poolInfo.electricityCost!) / counter
        : 0
  );

  let totalCostStandard = sumPositiveElements(standardCostPerMonth, months);
  let totalCostPremium = sumPositiveElements(premiumCostPerMonth, months);

  return {
    totalCostStandard,
    totalCostPremium,
    smallModelEnergyConsumption,
    bigModelEnergyConsumption,
    standardCostPerMonth,
    premiumCostPerMonth,
  };
};

function sumPositiveElements(array: number[], months: SeasonMonth[]): number {
  const trueIndexes = months
    .map((month, index) => (month.value ? index : -1))
    .filter((index) => index !== -1);

  const sum = array
    .filter((value, index) => value > 0 && trueIndexes.includes(index))
    .reduce((accumulator, currentValue) => accumulator + currentValue, 0);

  return sum;
}

const getStandardModelCOP = (
  averageTaxStandard: number[],
  cOP26Standard: { value100: number; value50: number; value20: number },
  copPerMonth: number[]
) => {
  const standardModelCOP = averageTaxStandard.map((tax, index) => {
    const monthCOPCoefficient = copPerMonth[index];

    if (0.2 < tax) {
      if (tax > 0.5) {
        const x37 = (cOP26Standard.value100 - cOP26Standard.value50) / 0.5;
        const x38 = cOP26Standard.value100 - x37;
        return (tax * x37 + x38) * monthCOPCoefficient;
      } else {
        const x40 = (cOP26Standard.value20 - cOP26Standard.value50) / -0.3;
        const x41 = x40 * -0.5 + cOP26Standard.value50;

        return (tax * x40 + x41) * monthCOPCoefficient;
      }
    } else {
      if (tax < 0) {
        return 0;
      } else {
        return cOP26Standard.value20 * monthCOPCoefficient;
      }
    }
  });

  return standardModelCOP;
};

const getPremiumModelCOP = (
  averageTaxStandard: number[],
  cOP26Standard: { value100: number; value50: number; value20: number },
  copPerMonth: number[]
) => {
  const premiumModelCOP = averageTaxStandard.map((tax, index) => {
    const U64 = copPerMonth[index];
    if (0.2 < tax) {
      if (tax > 0.5) {
        const x37 = (cOP26Standard.value100 - cOP26Standard.value50) / 0.5;
        const x38 = cOP26Standard.value100 - x37;
        return (tax * x37 + x38) * U64;
      } else {
        const x40 = (cOP26Standard.value20 - cOP26Standard.value50) / -0.3;
        const x41 = x40 * -0.5 + cOP26Standard.value50;

        return (tax * x40 + x41) * U64;
      }
    } else {
      if (tax < 0) {
        return 0;
      } else {
        return cOP26Standard.value20 * U64;
      }
    }
  });

  return premiumModelCOP;
};

const getCOPFromPumpData = (
  pumpCapacity: number,
  usesLowTemperatureCOP: boolean
): { value100: number; value50: number; value20: number } | undefined => {
  const pumpModel = pumpModels.find((pump) => {
    const capacityToCheck = pump.onAir26?.capacity || pump.onAir15.capacity;
    return capacityToCheck === pumpCapacity;
  });

  if (!pumpModel) {
    console.warn(`No pump model found with capacity ${pumpCapacity}`);
    // Find the nearest pump by capacity
    const sortedPumps = pumpModels
      .map((pump) => ({
        pump,
        capacity: pump.onAir26?.capacity || pump.onAir15.capacity,
        diff: Math.abs(
          (pump.onAir26?.capacity || pump.onAir15.capacity) - pumpCapacity
        ),
      }))
      .sort((a, b) => a.diff - b.diff);

    if (sortedPumps.length > 0) {
      const nearestPump = sortedPumps[0].pump;
      console.warn(
        `Using nearest pump: ${nearestPump.model} with capacity ${sortedPumps[0].capacity}`
      );
      const copData = nearestPump.onAir26 || nearestPump.onAir15;
      return {
        value100: copData.minimum,
        value50: (copData.minimum + copData.maximum) / 2,
        value20: copData.maximum,
      };
    }
    return undefined;
  }

  const copData = pumpModel.onAir26 || pumpModel.onAir15;

  if (!copData) {
    console.warn(`No COP data available for pump ${pumpModel.model}`);
    return undefined;
  }

  return {
    value100: copData.minimum,
    value50: (copData.minimum + copData.maximum) / 2,
    value20: copData.maximum,
  };
};

export const getFirstHeating = ({
  heatData,
  poolInfo,
  months,
  temperaturePerMonth,
  standartPumpCapacity,
  premiumPumpCapacity,
  measures,
  counter,
}: {
  heatData: HeatData;
  standartPumpCapacity: number | string;
  premiumPumpCapacity: number | string;
  poolInfo: PoolInfo;
  months: SeasonMonth[];
  measures: { surfaceAreaTop: number; estimateVol: number };
  temperaturePerMonth: { [key: number]: number };
  counter: number;
}) => {
  if (heatData?.maxHeatLoss! > 35) return { standart: 0, premium: 0 };
  if (
    typeof standartPumpCapacity === "string" ||
    standartPumpCapacity === 0 ||
    typeof premiumPumpCapacity === "string" ||
    premiumPumpCapacity === 0
  ) {
    return { totalCostPremium: 0, totalCostStandard: 0 };
  }

  const filteredMonths = months
    .map((month, index) => (month.value ? index : -1))
    .filter((index) => index !== -1);

  const kWhDuringHeatingPhase = months.map((_, index) => {
    const kwH = Math.ceil(
      (poolInfo.requirePoolTemp - temperaturePerMonth[index + 1]) *
        1.16 *
        measures.estimateVol
    );

    return kwH < 0 ? 0 : kwH;
  });
  const heatingTimes = filteredMonths.map((month) => {
    const timeToHeat =
      (kWhDuringHeatingPhase[month] / standartPumpCapacity) * 1.8;
    const standardFirstHeatingTime = timeToHeat / 24 / counter;
    const premiumFirstHeatingTime =
      ((standartPumpCapacity / premiumPumpCapacity) *
        standardFirstHeatingTime) /
      counter;
    return {
      month,
      standardFirstHeatingTime,
      premiumFirstHeatingTime,
    };
  });

  const minHeatingTime = heatingTimes.reduce(
    (min, curr) =>
      curr.standardFirstHeatingTime < min.standardFirstHeatingTime ? curr : min,
    heatingTimes[0]
  );
  const maxHeatingTime = heatingTimes.reduce(
    (max, curr) =>
      curr.standardFirstHeatingTime > max.standardFirstHeatingTime ? curr : max,
    heatingTimes[0]
  );

  return {
    firstHeatingStandard: {
      minMonth: minHeatingTime.month,
      maxMonth: maxHeatingTime.month,
      min: minHeatingTime.standardFirstHeatingTime * 24,
      max: maxHeatingTime.standardFirstHeatingTime * 24,
    },
    firstHeatingPremium: {
      minMonth: minHeatingTime.month,
      maxMonth: maxHeatingTime.month,
      min: minHeatingTime.premiumFirstHeatingTime * 24,
      max: maxHeatingTime.premiumFirstHeatingTime * 24,
    },
  };
};

interface IGetAnnualCOP {
  heatDemandLoss: number[];
  months: SeasonMonth[];
  smallModelEnergyConsumption: number[];
  bigModelEnergyConsumption: number[];
  counter: number;
}

export const getAnnualCOP = ({
  heatDemandLoss,
  months,
  bigModelEnergyConsumption,
  smallModelEnergyConsumption,
  counter,
}: IGetAnnualCOP) => {
  // Calculate monthly heat demand only for selected months
  const kcalNeededMonthly = heatDemandLoss.map((loss, index) => {
    // If the month is selected and there is heat loss, calculate demand
    return months[index].value && loss > 0 ? loss * 30 * 24 : 0;
  });

  // Total heat required annually (only selected months)
  const totalAnnualHeatDemand = kcalNeededMonthly.reduce(
    (accumulator, currentValue) => accumulator + currentValue,
    0
  );

  const totalSmallEnergyConsumption = sumPositiveElements(
    smallModelEnergyConsumption,
    months
  );

  const totalBigEnergyConsumption = sumPositiveElements(
    bigModelEnergyConsumption,
    months
  );

  // Prevent division by zero and ensure valid COP values
  const standardCop =
    totalSmallEnergyConsumption > 0
      ? totalAnnualHeatDemand / totalSmallEnergyConsumption
      : 0;

  const premiumCop =
    totalBigEnergyConsumption > 0
      ? totalAnnualHeatDemand / totalBigEnergyConsumption
      : 0;

  return {
    standardAnnualCop:
      isNaN(standardCop) || !isFinite(standardCop)
        ? 0
        : Number(standardCop.toFixed(2)),
    premiumAnnualCop:
      isNaN(premiumCop) || !isFinite(premiumCop)
        ? 0
        : Number(premiumCop.toFixed(2)),
  };
};

export function findLastTrueIndexInObjects(array: any) {
  for (let i = array.length - 1; i >= 0; i--) {
    if (array[i].value === true) {
      return i;
    }
  }
  return -1;
}

export function findObjWithSmallestCapacity(arr: any[]): any {
  if (arr.length === 0) return { capacity: 0, value1: 0, value2: 0 };

  return arr.reduce((acc, currentObj) => {
    return acc.capacity < currentObj.capacity ? acc : currentObj;
  });
}
